/**
 * Shared phase-progress utilities used by WorkspaceRightPanel.
 *
 * Extracted from the old PhaseStrip component so the pure logic
 * remains available after PhaseStrip itself was removed.
 */
import {
  PROGRESS_PHASE_LABEL_BY_NUMBER,
  type ProgressPhaseNumber,
} from '@volley/crucible-ipc-types/checkpoint-content';
import type { DerivedPhaseWire } from '@volley/crucible-ipc-types/ipc-channels';

function phaseLabel(phase: ProgressPhaseNumber): string {
  return PROGRESS_PHASE_LABEL_BY_NUMBER[phase];
}

export const PHASE_LABELS = [
  { phase: 1, short: phaseLabel(1), long: phaseLabel(1) },
  { phase: 2, short: phaseLabel(2), long: phaseLabel(2) },
  { phase: 3, short: phaseLabel(3), long: phaseLabel(3) },
  { phase: 4, short: phaseLabel(4), long: phaseLabel(4) },
  { phase: 5, short: phaseLabel(5), long: phaseLabel(5) },
  { phase: 6, short: phaseLabel(6), long: phaseLabel(6) },
  { phase: 7, short: phaseLabel(7), long: phaseLabel(7) },
] as const;

export function phaseLabelFromPhaseNumber(phaseNumber: number | null | undefined): string | null {
  const entry = PHASE_LABELS.find((phase) => phase.phase === phaseNumber);
  return entry?.long ?? null;
}

export type PhaseChipState = 'done' | 'done-partial' | 'in-progress' | 'pending' | 'blocked';

/** Count budget-skipped transitions for a given phase number. */
export function budgetSkipCountForPhase(
  transitions: DerivedPhaseWire['transitions'],
  phaseNumber: number,
): number {
  return transitions.filter((t) => t.phase === phaseNumber && t.budgetSkipped === true).length;
}

/**
 * Resolve the epoch-ms start time for the given phase by finding the
 * earliest (lowest seq) transition into that phase. Missing or malformed
 * transition timestamps return null so callers can omit unstable elapsed
 * timers instead of resetting them on every render.
 */
export function phaseStartedAtMs(
  transitions: DerivedPhaseWire['transitions'],
  phaseNumber: number,
): number | null {
  let earliest: (typeof transitions)[number] | undefined;
  for (const t of transitions) {
    if (t.phase !== phaseNumber) continue;
    if (!earliest || t.seq < earliest.seq) earliest = t;
  }
  if (!earliest) return null;
  const parsed = Date.parse(earliest.at);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Compute the visual state of each of the seven chips from a derived snapshot.
 *   phase < current  → 'done-partial' if any transition has budgetSkipped, else 'done'
 *   phase === current → 'blocked' if blockedBy set, else 'in-progress'
 *   phase > current  → 'pending'
 */
export function chipStatesFromDerived(derived: DerivedPhaseWire): readonly PhaseChipState[] {
  const current = derived.current;
  const blocked = derived.blockedBy !== undefined;
  return PHASE_LABELS.map((entry) => {
    if (entry.phase < current) {
      return budgetSkipCountForPhase(derived.transitions, entry.phase) > 0
        ? 'done-partial'
        : 'done';
    }
    if (entry.phase > current) return 'pending';
    return blocked ? 'blocked' : 'in-progress';
  });
}
